import * as THREE from 'three'

/**
 * 椭球体射线相交测试工具
 * 用于精确的高斯 splat 射线检测
 *
 * 高斯 splat 本质上是一个3D椭球体，由以下参数定义：
 * - center: 中心位置
 * - scale: 三个轴的缩放因子（log空间）
 * - rotation: 旋转四元数
 *
 * 椭球体方程：(p-c)^T * R * S^-2 * R^T * (p-c) <= 1
 * 其中 R 是旋转矩阵，S 是缩放矩阵
 */

/**
 * 椭球体定义
 */
export interface Ellipsoid {
  /** 椭球体中心；存在 matrixWorld 时为 splat 的 local-space 中心。 */
  center: THREE.Vector3
  /** 三个主轴半径，已从 log scale 转换 */
  radii: THREE.Vector3
  /** 椭球体旋转四元数 */
  rotation: THREE.Quaternion
  /** 可选的 local→world placement 变换，包含父级非均匀缩放。 */
  matrixWorld?: THREE.Matrix4
}

/**
 * 射线与椭球体相交结果
 */
export interface EllipsoidIntersection {
  /** 是否相交 */
  intersects: boolean
  /** 相交点与射线原点的距离 */
  distance: number
  /** 相交点世界坐标 */
  point: THREE.Vector3
  /** 相交点法线 */
  normal: THREE.Vector3
}

/**
 * 椭球体射线检测工具类
 */
export class EllipsoidRaycast {
  // 复用的临时对象，减少内存分配
  private static readonly _matrix = new THREE.Matrix4()
  private static readonly _invMatrix = new THREE.Matrix4()
  private static readonly _rayOrigin = new THREE.Vector3()
  private static readonly _rayDirection = new THREE.Vector3()
  private static readonly _temp = new THREE.Vector3()
  private static readonly _normalMatrix = new THREE.Matrix3()

  /**
   * 测试射线与椭球体相交
   *
   * @param ray 射线
   * @param ellipsoid 椭球体
   * @param threshold 距离阈值系数（用于放宽碰撞检测，默认1.0）
   * @returns 相交信息，如果不相交则 intersects 为 false
   */
  static intersect(
    ray: THREE.Ray,
    ellipsoid: Ellipsoid,
    threshold: number = 1.0
  ): EllipsoidIntersection {
    const result: EllipsoidIntersection = {
      intersects: false,
      distance: Infinity,
      point: new THREE.Vector3(),
      normal: new THREE.Vector3(),
    }

    // 构建变换矩阵：将椭球体变换为单位球
    // 1. 平移到原点
    // 2. 应用旋转的逆
    // 3. 应用缩放的逆
    const matrix = EllipsoidRaycast._matrix
    const invMatrix = EllipsoidRaycast._invMatrix

    // 缩放矩阵（应用阈值）
    const sx = 1.0 / (ellipsoid.radii.x * threshold)
    const sy = 1.0 / (ellipsoid.radii.y * threshold)
    const sz = 1.0 / (ellipsoid.radii.z * threshold)

    matrix.compose(ellipsoid.center, ellipsoid.rotation, new THREE.Vector3(1 / sx, 1 / sy, 1 / sz))
    if (ellipsoid.matrixWorld) matrix.premultiply(ellipsoid.matrixWorld)

    invMatrix.copy(matrix).invert()

    // 将射线变换到椭球体的局部空间（单位球空间）
    const rayOrigin = EllipsoidRaycast._rayOrigin.copy(ray.origin).applyMatrix4(invMatrix)
    const rayDirection = EllipsoidRaycast._rayDirection
      .copy(ray.direction)
      .transformDirection(invMatrix)
      .normalize()

    // 现在在单位球空间中求解射线与球的相交
    // 射线方程: p = o + t * d
    // 球方程: |p|^2 = 1
    // 代入得: |o + t*d|^2 = 1
    // 展开: (o + t*d)·(o + t*d) = 1
    // 即: a*t^2 + b*t + c = 0
    // 其中:
    //   a = d·d = 1 (因为d已归一化)
    //   b = 2 * o·d
    //   c = o·o - 1

    const a = rayDirection.dot(rayDirection) // 实际上等于1
    const b = 2.0 * rayOrigin.dot(rayDirection)
    const c = rayOrigin.dot(rayOrigin) - 1.0

    const discriminant = b * b - 4 * a * c

    // 无实根，不相交
    if (discriminant < 0) {
      return result
    }

    // 计算两个交点
    const sqrtDisc = Math.sqrt(discriminant)
    const t1 = (-b - sqrtDisc) / (2 * a)
    const t2 = (-b + sqrtDisc) / (2 * a)

    // 选择最近的正交点
    let t = -1
    if (t1 >= 0 && t2 >= 0) {
      t = Math.min(t1, t2)
    } else if (t1 >= 0) {
      t = t1
    } else if (t2 >= 0) {
      t = t2
    }

    if (t <= 0) {
      return result // 椭球体在射线后方
    }

    // 计算相交点（局部空间）
    const localPoint = EllipsoidRaycast._temp.copy(rayDirection).multiplyScalar(t).add(rayOrigin)

    // 变换回世界空间
    const worldPoint = localPoint.clone().applyMatrix4(matrix)

    // 计算法线（局部空间的法线就是归一化的位置向量）
    const localNormal = localPoint.clone().normalize()
    const worldNormal = localNormal
      .clone()
      .applyNormalMatrix(EllipsoidRaycast._normalMatrix.getNormalMatrix(matrix))
      .normalize()

    // 计算世界空间距离
    const distance = worldPoint.distanceTo(ray.origin)

    result.intersects = true
    result.distance = distance
    result.point = worldPoint
    result.normal = worldNormal

    return result
  }

  /**
   * 批量测试射线与多个椭球体相交
   *
   * @param ray 射线
   * @param ellipsoids 椭球体数组
   * @param threshold 距离阈值系数
   * @param maxResults 最大返回结果数
   * @returns 按距离排序的相交结果（包含索引）
   */
  static intersectMultiple(
    ray: THREE.Ray,
    ellipsoids: Ellipsoid[],
    threshold: number = 1.0,
    maxResults: number = 100
  ): Array<{ index: number; result: EllipsoidIntersection }> {
    const results: Array<{ index: number; result: EllipsoidIntersection }> = []

    for (let i = 0; i < ellipsoids.length; i++) {
      const result = EllipsoidRaycast.intersect(ray, ellipsoids[i], threshold)
      if (result.intersects) {
        results.push({ index: i, result })
      }

      // 早期退出：如果已经收集足够多的结果
      if (results.length >= maxResults * 2) {
        break
      }
    }

    // 按距离排序
    results.sort((a, b) => a.result.distance - b.result.distance)

    // 限制返回数量
    return results.slice(0, maxResults)
  }

  /**
   * 快速粗略相交测试（使用包围球）
   * 用于快速剔除明显不相交的情况
   *
   * @param ray 射线
   * @param center 椭球体中心
   * @param maxRadius 椭球体最大半径
   * @returns 是否可能相交
   */
  static roughIntersect(ray: THREE.Ray, center: THREE.Vector3, maxRadius: number): boolean {
    const distance = ray.distanceToPoint(center)
    return distance <= maxRadius
  }

  /**
   * 从 SplatData 构建椭球体
   *
   * @param center 中心位置
   * @param scale0 缩放因子 0 (log空间)
   * @param scale1 缩放因子 1 (log空间)
   * @param scale2 缩放因子 2 (log空间)
   * @param rot0 旋转四元数 w (PLY 存储格式：标量在前)
   * @param rot1 旋转四元数 x
   * @param rot2 旋转四元数 y
   * @param rot3 旋转四元数 z
   * @returns 椭球体对象
   */
  static fromSplatData(
    center: THREE.Vector3,
    scale0: number,
    scale1: number,
    scale2: number,
    rot0: number,
    rot1: number,
    rot2: number,
    rot3: number
  ): Ellipsoid {
    // 从 log 空间转换到实际半径
    const radii = new THREE.Vector3(Math.exp(scale0), Math.exp(scale1), Math.exp(scale2))

    // 四元数归一化
    // PLY 存储格式: rot_0=w, rot_1=x, rot_2=y, rot_3=z (标量在前)
    // THREE.Quaternion 构造函数: (x, y, z, w)
    const rotation = new THREE.Quaternion(rot1, rot2, rot3, rot0).normalize()

    return {
      center: center.clone(),
      radii,
      rotation,
    }
  }

  /**
   * 计算椭球体的包围球半径（用于快速剔除）
   *
   * @param ellipsoid 椭球体
   * @returns 包围球半径
   */
  static getBoundingSphereRadius(ellipsoid: Ellipsoid): number {
    return Math.max(ellipsoid.radii.x, ellipsoid.radii.y, ellipsoid.radii.z)
  }
}
